import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, ExternalLink, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';

const empty = {
  titulo: '',
  resumen: '',
  contenido: '',
  imagen: '',
  documento: '',
  categoria: 'General',
  tasa_interes: '',
  monto_maximo: '',
  plazo: '',
  orden: 0,
  estado: true,
};

export default function CreditosAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploadDocument, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  const documentoPath = watch('documento');

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/creditos', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    reset(empty);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    reset({
      titulo: row.titulo ?? '',
      resumen: row.resumen ?? '',
      contenido: row.contenido ?? '',
      imagen: row.imagen ?? '',
      documento: row.documento ?? '',
      categoria: row.categoria ?? 'General',
      tasa_interes: row.tasa_interes ?? '',
      monto_maximo: row.monto_maximo ?? '',
      plazo: row.plazo ?? '',
      orden: Number(row.orden) || 0,
      estado: Number(row.estado) === 1,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      ...values,
      orden: Number(values.orden) || 0,
      estado: values.estado ? 1 : 0,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/creditos/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/creditos', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar línea de crédito?', row.titulo);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/creditos/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleEstado(row) {
    try {
      const current = await api.get(`/v1/creditos/${row.id}`);
      const r = current.data?.data;
      if (!r) throw new Error('No se pudo leer el registro');
      const { data } = await api.put(`/v1/creditos/${row.id}`, {
        ...r,
        estado: Number(r.estado) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Imagen lista', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  async function onPickDocument(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadDocument(file);
      setValue('documento', path, { shouldValidate: true });
      toastSuccess('Documento listo', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  const columns = useMemo(
    () => [
      { key: 'orden', label: 'Orden' },
      { key: 'titulo', label: 'Título' },
      { key: 'categoria', label: 'Categoría' },
      { key: 'tasa_interes', label: 'Interés' },
      {
        key: 'documento',
        label: 'Documento',
        render: (row) =>
          row.documento ? (
            <a
              href={assetUrl(row.documento)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0d2b7f] hover:text-[#1acb12]"
            >
              <FileText className="h-4 w-4 shrink-0" aria-hidden />
              PDF
            </a>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        key: 'estado',
        label: 'Estado',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleEstado(row)}
            className={adminPublishStatusClasses(Number(row.estado) === 1)}
          >
            {Number(row.estado) === 1 ? 'Activo' : 'Inactivo'}
          </button>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Líneas de crédito</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Administra las líneas de crédito visibles en la página pública /creditos.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nueva línea
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar crédito' : 'Nueva línea de crédito'} wide>
        <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Título" error={errors.titulo?.message}>
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('titulo', { required: 'Requerido' })}
            />
          </FormField>
          <FormField label="Categoría">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('categoria')}
            />
          </FormField>
          <FormField label="Interés" hint="Texto libre, ej. 1.2% mensual">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('tasa_interes')}
            />
          </FormField>
          <FormField label="Monto máximo" hint="Texto libre, ej. Hasta $50.000.000">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('monto_maximo')}
            />
          </FormField>
          <FormField label="Plazo" hint="Texto libre, ej. Hasta 60 meses">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('plazo')}
            />
          </FormField>
          <FormField label="Orden en listado">
            <input
              type="number"
              min={0}
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('orden', { valueAsNumber: true })}
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Resumen">
              <textarea
                rows={2}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('resumen')}
              />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField label="Detalle (HTML permitido)" hint="Requisitos, beneficios y condiciones.">
              <textarea
                rows={10}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 font-mono text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('contenido')}
              />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField label="Imagen" hint={uploading ? 'Subiendo…' : undefined}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
                <input
                  type="text"
                  className="w-full flex-1 rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                  {...register('imagen')}
                />
              </div>
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField
              label="Documento (PDF)"
              hint={uploading ? 'Subiendo…' : 'Folleto o condiciones de la línea de crédito'}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={onPickDocument}
                  disabled={uploading}
                />
                <input
                  type="text"
                  className="w-full flex-1 rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                  {...register('documento')}
                />
              </div>
            </FormField>
            {documentoPath ? (
              <a
                href={assetUrl(documentoPath)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-accent-lime hover:text-white"
              >
                <ExternalLink className="h-4 w-4" aria-hidden />
                Abrir documento subido
              </a>
            ) : null}
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-300 md:col-span-2">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green" {...register('estado')} />
            Visible en web pública
          </label>
          <div className="flex flex-wrap gap-3 md:col-span-2">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Crear'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa" wide>
        {previewRow && (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
              {previewRow.imagen ? (
                <img src={assetUrl(previewRow.imagen)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">Sin imagen</div>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-lime">{previewRow.categoria}</p>
              <h3 className="font-display text-3xl font-bold text-white">{previewRow.titulo}</h3>
              <p className="text-sm text-slate-400">{previewRow.resumen}</p>
              <ul className="space-y-1 text-sm text-slate-300">
                {previewRow.tasa_interes ? <li>Interés: {previewRow.tasa_interes}</li> : null}
                {previewRow.monto_maximo ? <li>Monto: {previewRow.monto_maximo}</li> : null}
                {previewRow.plazo ? <li>Plazo: {previewRow.plazo}</li> : null}
              </ul>
              {previewRow.documento ? (
                <a
                  href={assetUrl(previewRow.documento)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#1acb12]/40 bg-[#1acb12]/10 px-4 py-2 text-sm font-semibold text-accent-lime hover:bg-[#1acb12]/20"
                >
                  <FileText className="h-4 w-4" aria-hidden />
                  Ver documento
                  <ExternalLink className="h-4 w-4" aria-hidden />
                </a>
              ) : null}
              <div
                className="max-w-none space-y-3 text-sm leading-relaxed text-slate-300 [&_li]:ml-4 [&_p]:text-slate-300"
                dangerouslySetInnerHTML={{ __html: previewRow.contenido || '' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
